import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { Camera, Smiley, SmileyMeh, SmileySad, FloppyDisk } from "@phosphor-icons/react";
import ExerciseProgress from "@/components/ExerciseProgress";
import NotificationSettings from "@/components/NotificationSettings";
import SleepHistory from "@/components/SleepHistory";
import ActivityLog from "@/components/ActivityLog";

const RATINGS = [
  { key: "sleep", label: "Sleep" },
  { key: "energy", label: "Energy" },
  { key: "libido", label: "Libido" },
  { key: "water_intake", label: "Water intake" },
  { key: "physical_activity", label: "Physical activity" },
  { key: "body_image", label: "Body image" },
  { key: "stress_levels", label: "Stress" },
];

export default function StatsPage() {
  const [searchParams] = useSearchParams();
  // Deep-link support:
  //   - `?wellness=1` (from Sunday wellness banner / push) → Wellness tab.
  //   - `?tab=body|wellness|profile` (from onboarding progress ring) → that tab.
  // Wellness param takes precedence for backwards compat.
  const _initialTab = searchParams.get("wellness") === "1"
    ? "wellness"
    : (["body", "wellness", "profile"].includes(searchParams.get("tab") || "")
        ? searchParams.get("tab")
        : "body");
  const [tab, setTab] = useState(_initialTab);
  const [profile, setProfile] = useState(null);
  const [body, setBody] = useState([]);
  const [wellness, setWellness] = useState([]);
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [p, b, w, s] = await Promise.all([
        http.get("/client/profile"),
        http.get("/client/body-stats"),
        http.get("/client/wellness"),
        http.get("/client/onboarding/status"),
      ]);
      setProfile(p.data); setBody(b.data); setWellness(w.data); setStatus(s.data);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="id-label mb-3">— Tracking —</div>
      <h1 className="id-h1 mb-10">My Stats</h1>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-4">{err}</div>}
      {msg && <div className="border border-[#FF3333]/40 bg-[#FF3333]/5 px-4 py-3 text-sm text-[#FF3333] mb-4" data-testid="stats-success">{msg}</div>}

      <NotificationSettings />

      <div className="mb-6">
        <SleepHistory />
      </div>

      <div className="mb-6">
        <ActivityLog />
      </div>

      {status && (status.body_due || status.wellness_due) && (
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm font-manrope text-zinc-200 mb-6" data-testid="stats-due-banner">
          {status.body_due && status.wellness_due
            ? "Both your weekly body check-in and fortnightly wellness rating are due. Fill them in below."
            : status.body_due
              ? "Your weekly body check-in is due. Submit your latest measurements."
              : "Your fortnightly wellness rating is due. Take 60 seconds to update it."}
        </div>
      )}

      <div className="flex gap-1 mb-6 border-b border-[#262626]">
        {[
          { k: "body", l: "Body (Weekly)" },
          { k: "wellness", l: "Wellness (Fortnightly)" },
          { k: "lifting", l: "Lifting" },
          { k: "profile", l: "Profile & Photos" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`stats-tab-${t.k}`}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold border-b-2 -mb-px transition-colors ${tab === t.k ? "text-white border-[#FF3333]" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "body" && <BodyTab body={body} reload={load} setMsg={setMsg} setErr={setErr} />}
      {tab === "wellness" && <WellnessTab wellness={wellness} reload={load} setMsg={setMsg} setErr={setErr} />}
      {tab === "lifting" && <ExerciseProgress fetchPath="/client/progress/exercises" />}
      {tab === "profile" && <ProfileTab profile={profile} reload={load} setMsg={setMsg} setErr={setErr} />}
    </div>
  );
}

function BodyTab({ body, reload, setMsg, setErr }) {
  const last = body[body.length - 1];
  const [w, setW] = useState(""); const [waist, setWaist] = useState("");
  const [leg, setLeg] = useState(""); const [arm, setArm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await http.post("/client/body-stats", {
        weight_kg: Number(w), waist_cm: Number(waist), upper_leg_cm: Number(leg), arm_cm: Number(arm),
      });
      setW(""); setWaist(""); setLeg(""); setArm("");
      setMsg("Body stats logged."); setTimeout(() => setMsg(""), 2500);
      await reload();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={save} className="id-card" data-testid="body-checkin-form">
        <div className="id-label mb-3">Log this week&apos;s measurements</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input label="Weight (kg)" value={w} set={setW} testid="body-weight" />
          <Input label="Waist (cm)" value={waist} set={setWaist} testid="body-waist" />
          <Input label="Upper leg (cm)" value={leg} set={setLeg} testid="body-leg" />
          <Input label="Bicep (cm)" value={arm} set={setArm} testid="body-arm" />
        </div>
        <button type="submit" disabled={saving} className="id-btn-primary mt-4" data-testid="body-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save check-in"}
        </button>
      </form>

      <div className="id-card">
        <div className="id-label mb-3">History · {body.length} entries</div>
        {body.length === 0 ? (
          <div className="text-zinc-500 font-manrope text-sm">Nothing logged yet.</div>
        ) : (
          <ul className="divide-y divide-[#262626]" data-testid="body-history">
            {[...body].reverse().map((b) => (
              <li key={b.id} className="py-3 grid grid-cols-5 gap-2 text-sm font-manrope">
                <div className="id-label">{(b.date || "").slice(0, 10)}</div>
                <div><span className="text-zinc-500 text-xs">Wt</span> {b.weight_kg}<span className="text-zinc-500"> kg</span></div>
                <div><span className="text-zinc-500 text-xs">Waist</span> {b.waist_cm}<span className="text-zinc-500"> cm</span></div>
                <div><span className="text-zinc-500 text-xs">Leg</span> {b.upper_leg_cm}<span className="text-zinc-500"> cm</span></div>
                <div><span className="text-zinc-500 text-xs">Arm</span> {b.arm_cm}<span className="text-zinc-500"> cm</span></div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function WellnessTab({ wellness, reload, setMsg, setErr }) {
  const [ratings, setRatings] = useState({ sleep: 5, energy: 5, libido: 5, water_intake: 5, physical_activity: 5, body_image: 5, stress_levels: 5 });
  const [mood, setMood] = useState("flat");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await http.post("/client/wellness", { ...ratings, mood });
      setMsg("Wellness check-in saved."); setTimeout(() => setMsg(""), 2500);
      await reload();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="id-card" data-testid="wellness-form">
        <div className="id-label mb-4">New wellness check-in (0 = worst, 10 = best)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {RATINGS.map((r) => (
            <div key={r.key} data-testid={`well-${r.key}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-oswald uppercase tracking-wide text-sm">{r.label}</span>
                <span className="font-oswald text-xl text-[#FF3333] font-bold">{ratings[r.key]}</span>
              </div>
              <input type="range" min={0} max={10} step={1} value={ratings[r.key]}
                onChange={(e) => setRatings((cur) => ({ ...cur, [r.key]: Number(e.target.value) }))}
                className="w-full accent-[#FF3333]" data-testid={`well-${r.key}-slider`} />
            </div>
          ))}
        </div>
        <div className="mt-5">
          <div className="id-label mb-2">Mood</div>
          <div className="grid grid-cols-3 gap-2" data-testid="well-mood">
            {[
              { v: "sad", Icon: SmileySad, l: "Sad" },
              { v: "flat", Icon: SmileyMeh, l: "Flat" },
              { v: "happy", Icon: Smiley, l: "Happy" },
            ].map(({ v, Icon, l }) => (
              <button key={v} type="button" onClick={() => setMood(v)} data-testid={`well-mood-${v}`}
                className={`py-3 border flex flex-col items-center gap-1 transition-colors ${mood === v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                <Icon size={24} weight="bold" />
                <span className="text-xs uppercase tracking-[0.15em] font-bold">{l}</span>
              </button>
            ))}
          </div>
        </div>
        <button onClick={save} disabled={saving} className="id-btn-primary mt-5" data-testid="well-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save wellness check-in"}
        </button>
      </div>

      <div className="id-card">
        <div className="id-label mb-3">History · {wellness.length} entries</div>
        {wellness.length === 0 ? (
          <div className="text-zinc-500 font-manrope text-sm">Nothing logged yet.</div>
        ) : (
          <ul className="divide-y divide-[#262626]" data-testid="wellness-history">
            {[...wellness].reverse().map((w) => (
              <li key={w.id} className="py-3 text-sm font-manrope">
                <div className="flex items-center justify-between">
                  <span className="id-label">{(w.date || "").slice(0, 10)}</span>
                  <span className="id-label text-[#FF3333]">{w.mood}</span>
                </div>
                <div className="mt-2 grid grid-cols-4 md:grid-cols-7 gap-1 text-[11px]">
                  {RATINGS.map((r) => (
                    <div key={r.key} className="border border-[#262626] px-2 py-1 text-center">
                      <div className="text-zinc-500 uppercase tracking-[0.1em]">{r.label.slice(0, 6)}</div>
                      <div className="text-white font-bold">{w[r.key]}</div>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ profile, reload, setMsg, setErr }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [heightCm, setHeightCm] = useState(""); const [sex, setSex] = useState("");
  const [occupation, setOccupation] = useState(""); const [saving, setSaving] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState("");
  useEffect(() => {
    if (profile) {
      setHeightCm(profile.height_cm || "");
      setSex(profile.sex || "");
      setOccupation(profile.occupation || "");
    }
  }, [profile]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await http.put("/client/profile", { height_cm: Number(heightCm) || null, sex: sex || null, occupation: occupation || null });
      setMsg("Profile saved."); setTimeout(() => setMsg(""), 2500);
      await reload();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  // Upload a body photo (front/side/back). Same endpoint as onboarding.
  const uploadPhoto = async (label, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Photo is too large. Please pick something under 8MB.");
      return;
    }
    setErr(""); setUploadingLabel(label);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await http.post("/client/profile/photos", {
            label,
            content_type: file.type || "image/jpeg",
            data: e.target.result,
          });
          setMsg(`${label[0].toUpperCase() + label.slice(1)} photo saved. Your coach can see it.`);
          setTimeout(() => setMsg(""), 3000);
          await reload();
        } catch (e) { setErr(apiError(e, "Upload failed.")); }
        finally { setUploadingLabel(""); }
      };
      reader.onerror = () => { setErr("Couldn't read the photo file."); setUploadingLabel(""); };
      reader.readAsDataURL(file);
    } catch (e) { setErr(apiError(e)); setUploadingLabel(""); }
  };

  const photos = profile?.starting_photos || [];
  // Find the currently uploaded id for a given label so we can show + allow replace
  const photoByLabel = (label) => photos.find((p) => (p.label || "").toLowerCase() === label);

  return (
    <div className="space-y-6">
      <div className="id-card">
        <div className="id-label mb-3">Personal details</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Height (cm)" value={heightCm} set={setHeightCm} testid="profile-height" />
          <div>
            <label className="id-label block mb-2">Sex</label>
            <select value={sex} onChange={(e) => setSex(e.target.value)} className="id-input" data-testid="profile-sex">
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="id-label block mb-2">Occupation</label>
            <input value={occupation} onChange={(e) => setOccupation(e.target.value)} className="id-input" data-testid="profile-occupation" />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="id-btn-primary mt-4" data-testid="profile-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="id-card">
        <div className="id-label mb-3">Starting photos</div>
        <p className="text-zinc-500 text-xs font-manrope mb-4">Front · side · back. Only your coach sees these. Tap a slot to add or replace.</p>
        <div className="grid grid-cols-3 gap-3" data-testid="profile-photos-grid">
          {["front", "side", "back"].map((label) => {
            const p = photoByLabel(label);
            const isUploading = uploadingLabel === label;
            return (
              <label
                key={label}
                className={`aspect-[3/4] border relative overflow-hidden bg-[#0F0F0F] cursor-pointer transition-colors block ${p ? "border-[#262626]" : "border-dashed border-[#333] hover:border-[#FF3333]/60"} ${isUploading ? "opacity-60" : ""}`}
                data-testid={`photo-slot-${label}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadPhoto(label, e.target.files?.[0])}
                  className="hidden"
                  disabled={isUploading}
                  data-testid={`photo-input-${label}`}
                />
                {p ? (
                  <>
                    <img
                      src={`${apiBase}/api/client/profile/photos/${p.id}`}
                      alt={label}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 id-label text-zinc-200 bg-[#0A0A0A]/80 px-2 py-0.5">{label}</div>
                    <div className="absolute bottom-2 right-2 id-label text-white bg-[#FF3333] px-2 py-0.5">Replace</div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-3">
                    <div className="id-label text-zinc-400 mb-2">{label}</div>
                    <div className="text-[10px] font-manrope text-zinc-600 uppercase tracking-[0.15em]">
                      {isUploading ? "Uploading…" : "Tap to add"}
                    </div>
                  </div>
                )}
              </label>
            );
          })}
        </div>
        <p className="text-zinc-600 text-[10px] font-manrope mt-3 uppercase tracking-[0.15em]">Max 8MB per photo · jpg/png/heic</p>
      </div>
    </div>
  );
}

function Input({ label, value, set, testid }) {
  return (
    <div>
      <label className="id-label block mb-2">{label}</label>
      <input type="number" step={0.1} value={value} onChange={(e) => set(e.target.value)} className="id-input" data-testid={testid} />
    </div>
  );
}
